/**
 * Contract for the Rust silence monitor's `AUDIO_CAPTURE_ACTIVITY` event
 * (`src-tauri/src/audio/record/consumers/silence_monitor.rs`).
 *
 * Rust emits with camelCase serde. `silent` fires once per silence episode
 * (20 s never-energetic / 45 s post-audio); `active` fires on recovery and the
 * monitor re-arms. The FE side (ipc.ts listener → `tauriRecordModel`) only
 * maintains state — all policy lives in `CaptureSilenceRecovery`.
 */

export const AUDIO_CAPTURE_ACTIVITY_EVENT = 'AUDIO_CAPTURE_ACTIVITY';

export type AudioCaptureSource = 'mic' | 'speaker';

export type AudioCaptureActivityState = 'silent' | 'active';

export type AudioCaptureActivityPayload = {
  source: AudioCaptureSource;
  state: AudioCaptureActivityState;
  /** Seconds of continuous silence when the event fired (0 on `active`). */
  silenceSecs: number;
  /** Whether this source has produced energetic audio since its producer spawned. */
  seenEnergetic: boolean;
  /**
   * Process-wide monotonic id, incremented per producer spawn. Events
   * carrying an id below the per-source high-water mark come from a torn-down
   * monitor and must be discarded so they can't poison a restarted recording.
   */
  captureInstanceId: number;
  sessionId?: string;
  /** Rust-side emission timestamp (ms since epoch). */
  ts: number;
  /**
   * Which recorder owns the capture this transition came from.
   *
   * `CaptureSilenceRecovery` resolves its session from the route and drives
   * server mutations with it, so an offline capture — which has no server
   * session at all — must never reach it. The frontend cannot work that out for
   * itself: `offlineActiveRecordingIdAtom` is written only after
   * `offline_recording_start` resolves and cleared before teardown finishes,
   * while the monitor is armed across both edges. So Rust says whose capture it
   * is at the emit site. See `CaptureOrigin` in
   * `src-tauri/src/audio/record/types.rs`.
   *
   * Present on every live capture, `'online'` included — which is what lets
   * `src/tauri/ipc.ts` trust the tag instead of the atom, and is the difference
   * between warning a clinician about a silent microphone and silently dropping
   * the event. Absent only on a desktop build older than the field, so its
   * absence means "unknown", never "online", and only then does the listener
   * fall back to the atom. Same contract as the `ERROR` event's
   * `capture_origin`, under this payload's camelCase serde spelling.
   */
  captureOrigin?: string;
};
